import * as THREE from 'three';
import { G, K_BEV, mesh } from '../render/scene';
import { MAT_P, coreGlow } from '../render/materials';
import { bevel, cable, joint, mark, plate, ram, rib, seam } from './mech-kit';
/* ============================================================
   核心 CORE：軀幹 + 雙肩承座（1:1 移植自 mech_sample_ani.html）
   ============================================================

   核心比例（local y0 = 腰線最窄處）：
     肩承座 x ±0.96　胸寬 1.95　腰寬 0.78　胸腰比 2.5
   闊背板從肩往內下收 → 正面剪影是 V 錐，這是「健體」的主要來源。 */

export function buildCore(shape, P?) {
  P = P || { glow: coreGlow(shape) };
  var g = new THREE.Group();
  mark(g, shape, ['core-crit', 'core-burn', 'core-std']);
  // 腰關節：不做實心腰柱。胸與骨盆之間要看得穿 —— 這是 AC 的機構語彙。
  g.add(mesh(G.cyl, MAT_P.trim, [0.30, 0.48, 0.30], [0, 0.04, 0]));                // 主軸
  g.add(mesh(G.tor, MAT_P.trim, [0.56, 0.56, 0.20], [0, 0.24, 0], [Math.PI / 2, 0, 0]));// 軸環
  g.add(ram([0.25, 0.06, 0.16], 0.46, 0.10, 0.085, [-0.14, 0, 0]));           // 前液壓 ×2
  g.add(ram([-0.25, 0.06, 0.16], 0.46, 0.10, 0.085, [-0.14, 0, 0]));
  g.add(cable([0.26, 0.16, -0.22], 0.34));                                    // 纜束橫過腰縫：只掛一邊

  if (shape === 'core-crit') {
    g.add(bevel(MAT_P.dark, 1.26, 1.02, 0.96, [0, 0.66, 0]));
    g.add(mesh(G.bev, P.glow, [0.44, 0.96, 0.44], [0, 0.70, 0.36]));        // 外露稜柱核心
    g.add(plate(MAT_P.armor, 0.66, 1.02, 0.28, [0.56, 0.72, 0.42], -0.10, -0.38, -0.10));
    g.add(plate(MAT_P.armor, 0.66, 1.02, 0.28, [-0.56, 0.72, 0.42], -0.10, 0.38, 0.10));
    g.add(rib(MAT_P.armor, 0.09, 1.02, 0.13, [0.32, 0.74, 0.56], -0.10, -0.38, -0.10));
    g.add(rib(MAT_P.armor, 0.09, 1.02, 0.13, [-0.32, 0.74, 0.56], -0.10, 0.38, 0.10));
    g.add(plate(MAT_P.armor, 0.26, 0.88, 0.74, [0.70, 0.46, 0.00], 0, -0.32, -0.18));  // 闊背
    g.add(plate(MAT_P.armor, 0.26, 0.88, 0.74, [-0.70, 0.46, 0.00], 0, 0.32, 0.18));
    g.add(plate(MAT_P.armor, 1.22, 0.50, 0.26, [0, 0.90, -0.44], 0.12));
    g.add(plate(MAT_P.armor, 1.06, 0.40, 0.24, [0, 0.36, -0.48], 0.02));
    g.add(mesh(G.cone, MAT_P.armor, [0.30, 0.88, 0.30], [0, 1.30, -0.20], [-0.24, 0, 0]));
    g.add(bevel(MAT_P.dark, 0.44, 0.62, 0.66, [0.94, 0.94, 0]));
    g.add(bevel(MAT_P.dark, 0.44, 0.62, 0.66, [-0.94, 0.94, 0]));
    g.add(mesh(G.cone, MAT_P.armor, [0.20, 0.54, 0.20], [1.03, 1.28, 0], [0, 0, -0.44]));
    g.add(plate(MAT_P.armor, 0.66, 0.18, 0.58, [0.54, 1.05, 0], 0, -0.16, -0.32));
    g.add(plate(MAT_P.armor, 0.66, 0.18, 0.58, [-0.54, 1.05, 0], 0, 0.16, 0.32));

  } else if (shape === 'core-burn') {
    g.add(bevel(MAT_P.dark, 1.50, 1.04, 1.08, [0, 0.66, 0]));
    g.add(plate(MAT_P.armor, 0.92, 0.44, 0.34, [0.50, 1.00, 0.40], -0.30, -0.10));
    g.add(plate(MAT_P.armor, 0.92, 0.44, 0.34, [-0.50, 1.00, 0.40], -0.30, 0.10));
    g.add(rib(MAT_P.armor, 0.12, 0.50, 0.16, [0, 1.00, 0.52], -0.30));           // 中央龍骨
    g.add(mesh(G.box, P.glow, [1.34, 0.20, 0.12], [0, 0.72, 0.54]));        // 大面積發光
    g.add(mesh(G.box, P.glow, [0.92, 0.14, 0.10], [0, 0.46, 0.50]));
    g.add(seam(1.44, 0.06, 0.10, [0, 0.86, 0.48], 0.10));
    g.add(plate(MAT_P.armor, 0.98, 0.34, 0.30, [0, 0.30, 0.42], 0.30));
    g.add(plate(MAT_P.armor, 0.30, 0.90, 0.76, [0.74, 0.50, 0.02], 0, -0.28, -0.14));
    g.add(plate(MAT_P.armor, 0.30, 0.90, 0.76, [-0.74, 0.50, 0.02], 0, 0.28, 0.14));
    g.add(plate(MAT_P.armor, 1.46, 0.11, 0.56, [0, 1.16, -0.42], 0.62));           // 散熱鰭 ×3
    g.add(plate(MAT_P.armor, 1.38, 0.11, 0.56, [0, 0.88, -0.48], 0.62));           // 高過肩線 → 氣場
    g.add(plate(MAT_P.armor, 1.28, 0.11, 0.56, [0, 0.60, -0.54], 0.62));
    g.add(mesh(G.cyl, MAT_P.trim, [0.28, 0.94, 0.28], [0, 0.68, -0.30]));
    g.add(bevel(MAT_P.dark, 0.48, 0.66, 0.72, [0.98, 0.94, 0]));
    g.add(bevel(MAT_P.dark, 0.48, 0.66, 0.72, [-0.98, 0.94, 0]));
    g.add(plate(MAT_P.armor, 0.72, 0.20, 0.62, [0.58, 1.05, 0], 0, -0.16, -0.28));
    g.add(plate(MAT_P.armor, 0.72, 0.20, 0.62, [-0.58, 1.05, 0], 0, 0.16, 0.28));

  } else {  /* core-std */
    g.add(bevel(MAT_P.dark, 1.44, 1.02, 1.04, [0, 0.66, 0]));                     // 胸腔內部
    g.add(plate(MAT_P.armor, 0.86, 0.70, 0.36, [0.52, 0.88, 0.42], -0.26, -0.12));// 胸甲分左右
    g.add(plate(MAT_P.armor, 0.86, 0.70, 0.36, [-0.52, 0.88, 0.42], -0.26, 0.12));
    g.add(rib(MAT_P.armor, 0.13, 0.92, 0.18, [0, 0.84, 0.54], -0.24));           // 中央龍骨（亮線）
    g.add(plate(MAT_P.armor, 0.28, 0.92, 0.76, [0.72, 0.50, 0.02], 0, -0.30, -0.16)); // 闊背 → V 錐
    g.add(plate(MAT_P.armor, 0.28, 0.92, 0.76, [-0.72, 0.50, 0.02], 0, 0.30, 0.16));
    g.add(plate(MAT_P.armor, 0.94, 0.30, 0.30, [0, 0.30, 0.40], 0.30));            // 腹甲
    g.add(seam(1.34, 0.05, 0.10, [0, 0.50, 0.48], 0.12));                     // 胸下縫
    g.add(mesh(G.cyl, P.glow, [0.34, 0.14, 0.34], [0, 0.68, 0.56], [Math.PI / 2, 0, 0]));
    g.add(mesh(G.tor, MAT_P.trim, [0.58, 0.58, 0.30], [0, 0.68, 0.56]));
    g.add(plate(MAT_P.armor, 1.30, 0.52, 0.28, [0, 0.90, -0.46], 0.12));           // 背板上段
    g.add(plate(MAT_P.armor, 1.14, 0.42, 0.26, [0, 0.36, -0.50], 0.02));           // 下段：中間留穿透的縫
    g.add(bevel(MAT_P.dark, 0.46, 0.66, 0.70, [0.96, 0.94, 0]));                 // 肩承座
    g.add(bevel(MAT_P.dark, 0.46, 0.66, 0.70, [-0.96, 0.94, 0]));
    g.add(plate(MAT_P.armor, 0.70, 0.20, 0.62, [0.56, 1.05, 0], 0, -0.16, -0.30));// 斜方肌斜面
    g.add(plate(MAT_P.armor, 0.70, 0.20, 0.62, [-0.56, 1.05, 0], 0, 0.16, 0.30));
  }
  return g;
}
